<template>

  <DefaultCrudView
    @switch-inactive="switchInactive"
    @create-new="newEnabler"
    :breadcrumbsItems="breadcrumbs"
    :actions="crudActions"
    :inactiveFilterValue="inactiveFilterValue"
    showInactiveFilter
    containerLarge
  >
    <template slot="content">

      <v-data-table
        :headers="headers"
        :items="enablers"
        class="elevation-1"
        :loading="loading"
        :options.sync="options"
        :server-items-length="itemsLength"
        :item-class="itemRowStyle"
        :footer-props="$defaultDataTableFooterProps"
      >
        <template v-slot:item.tenant_id="{ item }">
          {{ getTenantTitle(item.tenant_id) }}
        </template>

        <template v-slot:item.enabler_type="{ item }">
          <span
            color="primary"
            dark
          >
            {{ item.enabler_type }}
          </span>
        </template>

        <template v-slot:item.actions="{ item }">

          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                class="mr-2"
                @click="editEnabler(item)"
              >
                mdi-pencil
              </v-icon>
            </template>
            <span>{{ $t('edit') }}</span>
          </v-tooltip>

          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                class="mr-2"
                @click.stop="editEnablerFilter(item)"
              >
                mdi-filter-plus
              </v-icon>
            </template>
            <span>{{ $t('edit_filter') }}</span>
          </v-tooltip>

        </template>

      </v-data-table>

      <DefaultModal :title="getModalTitle()" ref="modal" v-on:submit="save">
        <template v-slot:content>
          <DynamicFormContent :fields="fields"></DynamicFormContent>
        </template>
      </DefaultModal>

      <FilterAssocModal 
        @submit-filter="saveEnablerFilter"
        ref="filter_assoc_modal"
      />

    </template>
  
  </DefaultCrudView>
</template>

<style scoped>
  ::v-deep .inactive-row {
    color: #616161;
    opacity: 0.6;
  }

  .container {
    padding: 0;
  }

  .table-container {
    margin-top: 20px;
  }
</style>

<script>

import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'

import DefaultCrudView from '@/components/DefaultCrudView.vue'
import DefaultModal from '@/components/DefaultModal.vue'
import DynamicFormContent from '@/components/DynamicFormContent.vue'
import FilterAssocModal from '@/components/node/FilterAssocModal'

import EnablerActions from '@/store/operation/enabler/actions-types'
import EnablerMutations from '@/store/operation/enabler/mutations-types'
import AppActions from '@/store/app/actions-types'
import TenantActions from '@/store/core/tenant/actions-types'

export default {
  name: "Enabler",

  components: {
    DefaultCrudView,
    DefaultModal,
    DynamicFormContent,
    FilterAssocModal
  },

  computed: {
    ...mapGetters('app', [
      'getIconForOperationSettingsItem'
    ]),

    ...mapState({
      fields: state => state.operation.enabler.fields,
      activesets: state => state.operation.enabler.enablers,
      headers: state => state.operation.enabler.tableHeaders,
      selectedEnabler: state => state.operation.enabler.selectedEnabler
    }),

    breadcrumbs() {
      return [
        {
          text: this.$t('enablers'),
          prependIcon: this.getIconForOperationSettingsItem('enablers'),
        },
      ]
    }
  },

  data() {
    return {
      inactiveFilterValue: ['false', 'all'].includes(this.$route.query['filter[active]']),
      loading: true,
      enablers: [],
      options: {
        sortBy: ['aspect_key'],
        sortDesc: [false],
      },
      itemsLength: 0,
      crudActions: [
        {
          text: "create_enabler",
          eventToEmit: "create-new",
        }
      ],
      tenants: [],
    }
  },

  mounted() {
    this[EnablerMutations.SET_SELECTED_ENABLER](null)

    this[TenantActions.GET_ALL_TENANTS]()
      .then((tenants) => {
        this[EnablerMutations.SET_FIELD_TENANTS](tenants.items)
        this.tenants = tenants.items
      })
  },

  watch: {
    inactiveFilterValue() {
      if (this.options.page !== 1) {
        this.options.page = 1
      } else {
        this.reloadData()
      }
    },

    options: {
      handler () {
        this.reloadData()
      },
      deep: true,
    },
  },

  methods: {
    switchInactive() {
      this.inactiveFilterValue = !this.inactiveFilterValue
    },

    itemRowStyle(item) {
      return item.active ? '' : 'inactive-row'
    },

    ...mapActions('operation/enabler', [
      EnablerActions.GET_ALL_ENABLERS,
      EnablerActions.SAVE_ENABLER,
      EnablerActions.UPDATE_ENABLER,
    ]),

    ...mapActions('core/tenant', [
      TenantActions.GET_ALL_TENANTS
    ]),

    ...mapActions('app', [
      AppActions.OPEN_APP_SUCCESS_MESSAGE
    ]),

    ...mapMutations('operation/enabler', [
      EnablerMutations.SET_FIELD_ENABLERS,
      EnablerMutations.SET_SELECTED_ENABLER,
      EnablerMutations.SET_FIELD_TENANTS,
      EnablerMutations.SET_FIELD_TYPES,
      EnablerMutations.SET_FIELD_PARAMS
    ]),

    newEnabler() {
      this[EnablerMutations.SET_SELECTED_ENABLER](null)
      this.$refs.modal.openModal()
    },

    save() {
      this.$refs.modal.errorMessage = null
      this.$refs.modal.errorList = []

      this[EnablerActions.SAVE_ENABLER]()
        .then(() => {
          let message = this.selectedEnabler ? 'Enabler edited' : 'Enabler created'
          this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t(message))
          this.$refs.modal.closeModal()
          this.reloadData()
        })
        .catch(() => this.$refs.modal.submitting = false)
    },

    reloadData() {
      let options = {...this.$route.query, 'filter[active]': this.inactiveFilterValue ? 'all' : 'true'}

      if (this.options.sortBy.length > 0) {
        options.order = this.options.sortBy[0]
      }

      if (this.options.sortDesc.length > 0 && this.options.sortDesc[0]) {
        options.direction = 'DESC'
      }

      options.limit = this.options.itemsPerPage
      options.offset = this.options.itemsPerPage * (this.options.page - 1)
      this.loading = true
      this[EnablerActions.GET_ALL_ENABLERS](options)
        .then((result) => {
          this.enablers = result.items
          this.itemsLength = result.total
          this.loading = false
        })
        .catch(() => this.loading = false)
    },

    editEnabler(enabler) {
      this[EnablerMutations.SET_SELECTED_ENABLER](enabler)
      this.$refs.modal.openModal()
    },

    editEnablerFilter(enabler) {
      this[EnablerMutations.SET_SELECTED_ENABLER](enabler)

      this.$refs.filter_assoc_modal.tenantIdFilter = enabler.tenant_id

      this.$refs.filter_assoc_modal.open(enabler.filter_id)
    },

    saveEnablerFilter(form) {
      const params = {
        filter_id: parseInt(form.get('filter')) || null
      }

      this[EnablerActions.UPDATE_ENABLER](params)
        .then(() => {
          this.$refs.filter_assoc_modal.loading = false

          this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t('filter_updated'))

          this.$refs.filter_assoc_modal.close()

          this.reloadData()
        })
        .catch(() => this.$refs.filter_assoc_modal.loading = false)
    },

    getTenantTitle(id) {
      let tenant = this.tenants.find(f => f.tenant_id == id)

      return tenant ? tenant.title : ''
    },

    getModalTitle() {
      return this.selectedEnabler ? this.$t('edit_enabler') : this.$t('new_enabler')
    }
  }
}

</script>